"use client";

import { useEffect, useState, type CSSProperties } from "react";
import {
    ChevronRight,
    Mail,
    MessageCircle,
    MessageSquare,
    Minus,
    Phone,
    X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ContactDetails } from "../../../types/business-content.types";
import {
    buildSmsEnquiryHref,
    buildWhatsAppEnquiryHref,
    normalisePhoneNumberForSms,
} from "../../products/ProductInfoCard/ProductInfoCard.helpers";

export type StickyContactMethod = "call" | "email" | "sms" | "whatsapp";
export type StickyContactPosition = "bottom-left" | "bottom-right";

export type StickyContactColors = {
    accent?: string;
    call?: string;
    email?: string;
    sms?: string;
    whatsapp?: string;
    background?: string;
    surface?: string;
    text?: string;
    mutedText?: string;
    border?: string;
    shadow?: string;
    closeText?: string;
    closeBackground?: string;
};

export type StickyContactLabels = {
    title?: string;
    description?: string;
    callTitle?: string;
    emailTitle?: string;
    smsTitle?: string;
    whatsappTitle?: string;
    toggleLabel?: string;
    closeLabel?: string;
};

export type StickyContactProps = {
    contactDetails?: ContactDetails;
    phoneNumber?: string;
    smsNumber?: string;
    whatsappNumber?: string;
    email?: string;
    businessName?: string;
    pageUrl?: string;
    smsMessage?: string;
    whatsappMessage?: string;
    emailSubject?: string;
    emailBody?: string;
    methods?: StickyContactMethod[];
    showCall?: boolean;
    showEmail?: boolean;
    showSms?: boolean;
    showWhatsApp?: boolean;
    title?: string;
    description?: string;
    labels?: StickyContactLabels;
    colors?: StickyContactColors;
    colours?: StickyContactColors;
    accentColor?: string;
    backgroundColor?: string;
    surfaceColor?: string;
    textColor?: string;
    borderColor?: string;
    opacity?: number;
    backgroundOpacity?: number;
    surfaceOpacity?: number;
    bottomOffset?: number | string;
    defaultCollapsed?: boolean;
    desktopCollapsed?: boolean;
    position?: StickyContactPosition;
    className?: string;
    style?: CSSProperties;
    initiallyOpen?: boolean;
    showCloseButton?: boolean;
    variant?: "dock" | "modal";
};

type CssVarStyle = CSSProperties & Record<`--${string}`, string | number | undefined>;

type StickyContactAction = {
    method: StickyContactMethod;
    title: string;
    value: string;
    href: string;
    icon: LucideIcon;
    color: string;
    ariaLabel: string;
    target?: string;
    rel?: string;
};

const defaultMethods: StickyContactMethod[] = [
    "call",
    "email",
    "sms",
    "whatsapp",
];

const defaultColors: Required<StickyContactColors> = {
    accent: "#c39958",
    call: "#111827",
    email: "#111827",
    sms: "#111827",
    whatsapp: "#111827",
    background: "#ffffff",
    surface: "#f6f6f6",
    text: "#171717",
    mutedText: "rgba(23, 23, 23, 0.62)",
    border: "rgba(23, 23, 23, 0.1)",
    shadow: "0 16px 38px rgba(15, 23, 42, 0.18)",
    closeText: "#171717",
    closeBackground: "rgba(23, 23, 23, 0.06)",
};

const defaultLabels: Required<StickyContactLabels> = {
    title: "Contact Us",
    description: "Choose the best way to reach us.",
    callTitle: "Call Us",
    emailTitle: "Email Us",
    smsTitle: "Send an SMS",
    whatsappTitle: "WhatsApp Us",
    toggleLabel: "Toggle contact options",
    closeLabel: "Close contact options",
};

const cleanValue = (value?: string) => value?.trim() || "";

const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

const hexToRgba = (value: string, alpha: number) => {
    const hex = value.trim().replace("#", "");
    const isShortHex = /^[0-9a-f]{3}$/i.test(hex);
    const isLongHex = /^[0-9a-f]{6}$/i.test(hex);

    if (!isShortHex && !isLongHex) return "";

    const fullHex = isShortHex
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex;

    const red = Number.parseInt(fullHex.slice(0, 2), 16);
    const green = Number.parseInt(fullHex.slice(2, 4), 16);
    const blue = Number.parseInt(fullHex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const colorWithOpacity = (color: string, opacity: number) => {
    const resolvedOpacity = clamp(opacity, 0, 1);
    if (resolvedOpacity >= 1) return color;

    return (
        hexToRgba(color, resolvedOpacity) ||
        `color-mix(in srgb, ${color} ${Math.round(resolvedOpacity * 100)}%, transparent)`
    );
};

const defaultBottomOffset = "1.5rem";

const toCssSize = (value?: number | string) => {
    if (typeof value === "number") return `${value}px`;
    return value;
};

const buildTelHref = (phoneNumber: string) => {
    const phone = normalisePhoneNumberForSms(phoneNumber);
    return phone ? `tel:${phone}` : "";
};

const buildMailtoHref = (email: string, subject: string, body: string) => {
    const safeEmail = cleanValue(email);

    if (!safeEmail) return "";

    const params = new URLSearchParams();
    if (subject) params.set("subject", subject);
    if (body) params.set("body", body);

    const query = params.toString();
    return `mailto:${safeEmail}${query ? `?${query}` : ""}`;
};

const isVisible = (
    method: StickyContactMethod,
    methods: StickyContactMethod[],
    show: boolean,
) => methods.includes(method) && show;

const StickyContact = ({
                           contactDetails,
                           phoneNumber,
                           smsNumber,
                           whatsappNumber,
                           email,
                           businessName,
                           pageUrl,
                           smsMessage,
                           whatsappMessage,
                           emailSubject = "New enquiry",
                           emailBody,
                           methods = defaultMethods,
                           showCall = true,
                           showEmail = true,
                           showSms = true,
                           showWhatsApp = true,
                           title,
                           description,
                           labels,
                           colors,
                           colours,
                           accentColor,
                           backgroundColor,
                           surfaceColor,
                           textColor,
                           borderColor,
                           opacity = 1,
                           backgroundOpacity = 1,
                           surfaceOpacity = 1,
                           bottomOffset = defaultBottomOffset,
                           defaultCollapsed = true,
                           desktopCollapsed,
                           position = "bottom-right",
                           className = "",
                           style,
                           initiallyOpen = true,
                           showCloseButton = true,
                           variant = "dock",
                       }: StickyContactProps) => {
    const initialCollapsed = desktopCollapsed ?? defaultCollapsed;
    const [isDismissed, setIsDismissed] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

    useEffect(() => {
        setIsCollapsed(initialCollapsed);
    }, [initialCollapsed]);

    if (!initiallyOpen || isDismissed) return null;

    const mergedLabels = {
        ...defaultLabels,
        ...labels,
    };

    const mergedColors = {
        ...defaultColors,
        ...colours,
        ...colors,
    };

    const accent = accentColor || mergedColors.accent;
    const panelTitle = title || mergedLabels.title;
    const panelDescription = description || mergedLabels.description;
    const displayPhone = cleanValue(phoneNumber || contactDetails?.contactNumberOne);
    const displaySmsNumber = cleanValue(smsNumber || displayPhone);
    const displayWhatsAppNumber = cleanValue(whatsappNumber || displayPhone);
    const displayEmail = cleanValue(email || contactDetails?.contactEmail);
    const defaultMessage = pageUrl
        ? `Hi, I would like to make an enquiry. ${pageUrl}`
        : "Hi, I would like to make an enquiry.";

    const allActions: StickyContactAction[] = [
        {
            method: "call",
            title: mergedLabels.callTitle,
            value: displayPhone,
            href: buildTelHref(displayPhone),
            icon: Phone,
            color: mergedColors.call || accent,
            ariaLabel: businessName ? `Call ${businessName}` : `Call ${displayPhone}`,
        },
        {
            method: "email",
            title: mergedLabels.emailTitle,
            value: displayEmail,
            href: buildMailtoHref(
                displayEmail,
                emailSubject,
                emailBody || defaultMessage,
            ),
            icon: Mail,
            color: mergedColors.email || accent,
            ariaLabel: businessName ? `Email ${businessName}` : `Email ${displayEmail}`,
        },
        {
            method: "sms",
            title: mergedLabels.smsTitle,
            value: displaySmsNumber,
            href: buildSmsEnquiryHref(displaySmsNumber, smsMessage || defaultMessage),
            icon: MessageSquare,
            color: mergedColors.sms || accent,
            ariaLabel: businessName
                ? `Send an SMS to ${businessName}`
                : `Send an SMS to ${displaySmsNumber}`,
        },
        {
            method: "whatsapp",
            title: mergedLabels.whatsappTitle,
            value: displayWhatsAppNumber,
            href: buildWhatsAppEnquiryHref(
                displayWhatsAppNumber,
                whatsappMessage || defaultMessage,
            ),
            icon: MessageCircle,
            color: mergedColors.whatsapp || accent,
            target: "_blank",
            rel: "noopener noreferrer",
            ariaLabel: businessName
                ? `Open WhatsApp for ${businessName}`
                : `Open WhatsApp for ${displayWhatsAppNumber}`,
        },
    ];

    const actions = allActions.filter((action) => {
        if (!action.value || !action.href) return false;

        if (action.method === "call") return isVisible(action.method, methods, showCall);
        if (action.method === "email") return isVisible(action.method, methods, showEmail);
        if (action.method === "sms") return isVisible(action.method, methods, showSms);
        return isVisible(action.method, methods, showWhatsApp);
    });

    if (!actions.length) return null;

    const resolvedBackground = backgroundColor || mergedColors.background;
    const resolvedSurface = surfaceColor || mergedColors.surface;
    const resolvedOpacity = clamp(opacity, 0, 1);
    const useDesktopColumns = actions.length > 2;

    const rootStyle: CssVarStyle = {
        "--sticky-contact-accent": accent,
        "--sticky-contact-panel-width": useDesktopColumns ? "38rem" : "20rem",
        "--sticky-contact-background": colorWithOpacity(
            resolvedBackground,
            backgroundOpacity,
        ),
        "--sticky-contact-surface": colorWithOpacity(resolvedSurface, surfaceOpacity),
        "--sticky-contact-text": textColor || mergedColors.text,
        "--sticky-contact-muted-text": mergedColors.mutedText,
        "--sticky-contact-border": borderColor || mergedColors.border,
        "--sticky-contact-shadow": mergedColors.shadow,
        "--sticky-contact-close-text": mergedColors.closeText,
        "--sticky-contact-close-background": mergedColors.closeBackground,
        left: position === "bottom-left" ? "0.75rem" : "auto",
        right: position === "bottom-right" ? "0.75rem" : "auto",
        bottom: `calc(${toCssSize(bottomOffset) || defaultBottomOffset} + env(safe-area-inset-bottom))`,
        margin: 0,
        opacity: resolvedOpacity,
        ...style,
    };

    const rootAriaLabel = businessName ? `Contact ${businessName}` : panelTitle;
    const dismissContact = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        setIsDismissed(true);
    };

    const renderCloseControl = (controlClassName: string) => {
        if (!showCloseButton) return null;

        const button = (
            <button
                type="button"
                onClick={dismissContact}
                aria-label={mergedLabels.closeLabel}
                className="sticky-contact-close"
            >
                <X size={17} strokeWidth={2.4} aria-hidden="true" />
            </button>
        );

        return <span className={controlClassName}>{button}</span>;
    };

    const content = (
        <>
            <details
                className="sticky-contact-shell"
                open={!isCollapsed}
                onToggle={(event) => {
                    setIsCollapsed(!event.currentTarget.open);
                }}
            >
                <summary
                    className="sticky-contact-summary"
                    aria-label={mergedLabels.toggleLabel}
                    aria-expanded={!isCollapsed}
                    title={mergedLabels.toggleLabel}
                >
                    <span className="sticky-contact-summary-icon" aria-hidden="true">
                        <MessageCircle size={18} strokeWidth={2.35} />
                    </span>
                    <span className="sticky-contact-summary-title">{panelTitle}</span>
                    <ChevronRight
                        className="sticky-contact-summary-chevron"
                        size={17}
                        strokeWidth={2.4}
                        aria-hidden="true"
                    />
                    <Minus
                        className="sticky-contact-summary-minimise"
                        size={17}
                        strokeWidth={2.4}
                        aria-hidden="true"
                    />
                </summary>

                <div className="sticky-contact-panel">
                    {renderCloseControl("sticky-contact-panel-close-form")}

                    <div>
                        <p className="sticky-contact-title">{panelTitle}</p>
                        <p className="sticky-contact-description">{panelDescription}</p>
                    </div>

                    <div
                        className={`sticky-contact-actions ${
                            useDesktopColumns ? "sticky-contact-actions--columns" : ""
                        }`.trim()}
                    >
                        {actions.map((action) => {
                            const Icon = action.icon;
                            const actionStyle: CssVarStyle = {
                                "--sticky-contact-method-color": action.color,
                            };

                            return (
                                <a
                                    key={action.method}
                                    href={action.href}
                                    target={action.target}
                                    rel={action.rel}
                                    aria-label={action.ariaLabel}
                                    className="sticky-contact-action"
                                    style={actionStyle}
                                >
                                    <span className="sticky-contact-action-icon" aria-hidden="true">
                                        <Icon size={20} strokeWidth={2.4} />
                                    </span>
                                    <span className="sticky-contact-action-copy">
                                        <span className="sticky-contact-action-title">
                                            {action.title}
                                        </span>
                                        <span className="sticky-contact-action-value">
                                            {action.value}
                                        </span>
                                    </span>
                                    <ChevronRight
                                        className="sticky-contact-action-chevron"
                                        size={16}
                                        strokeWidth={2.4}
                                        aria-hidden="true"
                                    />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </details>

            <div className="sticky-contact-dock" aria-label={panelTitle}>
                {actions.map((action) => {
                    const Icon = action.icon;
                    const actionStyle: CssVarStyle = {
                        "--sticky-contact-method-color": action.color,
                    };

                    return (
                        <a
                            key={action.method}
                            href={action.href}
                            target={action.target}
                            rel={action.rel}
                            title={action.title}
                            aria-label={action.ariaLabel}
                            className="sticky-contact-dock-action"
                            style={actionStyle}
                        >
                            <Icon size={19} strokeWidth={2.35} aria-hidden="true" />
                        </a>
                    );
                })}

                {renderCloseControl("sticky-contact-dock-close-form")}
            </div>
        </>
    );

    if (variant === "modal") {
        return (
            <dialog
                open
                aria-label={rootAriaLabel}
                data-component="sticky-contact"
                className={`sticky-contact-dialog ${className}`.trim()}
                style={rootStyle}
            >
                {content}
            </dialog>
        );
    }

    return (
        <aside
            aria-label={rootAriaLabel}
            data-component="sticky-contact"
            className={`sticky-contact-dialog ${className}`.trim()}
            style={rootStyle}
        >
            {content}
        </aside>
    );
};

export default StickyContact;
